import { useSyncExternalStore } from "react";

type PageActivityListener = () => void;

const BACKGROUND_INTERACTION_LEASE_MS = 6000;
const passiveCaptureOptions: AddEventListenerOptions = {
  capture: true,
  passive: true,
};

const listeners = new Set<PageActivityListener>();
let backgroundInteractionUntil = 0;
let backgroundInteractionTimer = 0;
let currentPageActivity =
  typeof document !== "undefined" &&
  document.visibilityState === "visible" &&
  document.hasFocus();
let listening = false;

function pageIsVisible() {
  return typeof document !== "undefined" && document.visibilityState === "visible";
}

function readPageActivity() {
  if (typeof document === "undefined") return false;

  return pageIsVisible() && (
    document.hasFocus() || Date.now() < backgroundInteractionUntil
  );
}

function publishPageActivity(nextActivity = readPageActivity()) {
  if (nextActivity === currentPageActivity) return;

  currentPageActivity = nextActivity;
  listeners.forEach((listener) => listener());
}

function activateFromBrowserState() {
  publishPageActivity(readPageActivity());
}

function handleVisibilityChange() {
  if (pageIsVisible()) {
    activateFromBrowserState();
  } else {
    deactivatePage();
  }
}

function clearBackgroundInteractionLease() {
  backgroundInteractionUntil = 0;

  if (backgroundInteractionTimer) {
    window.clearTimeout(backgroundInteractionTimer);
    backgroundInteractionTimer = 0;
  }
}

function deactivatePage() {
  clearBackgroundInteractionLease();
  publishPageActivity(false);
}

function expireBackgroundInteractionLease() {
  backgroundInteractionTimer = 0;

  const remaining = backgroundInteractionUntil - Date.now();
  if (remaining > 0) {
    backgroundInteractionTimer = window.setTimeout(
      expireBackgroundInteractionLease,
      remaining,
    );
    return;
  }

  backgroundInteractionUntil = 0;
  publishPageActivity(readPageActivity());
}

function activateFromBackgroundInteraction() {
  if (!pageIsVisible() || document.hasFocus()) return;

  // macOS can send wheel/scroll input to a visible Chrome window without
  // bringing that window to the foreground. Keep animation alive only while
  // that interaction is recent, then return to the low-CPU paused state.
  backgroundInteractionUntil = Date.now() + BACKGROUND_INTERACTION_LEASE_MS;
  publishPageActivity(true);

  if (!backgroundInteractionTimer) {
    backgroundInteractionTimer = window.setTimeout(
      expireBackgroundInteractionLease,
      BACKGROUND_INTERACTION_LEASE_MS,
    );
  }
}

function startListening() {
  if (listening || typeof window === "undefined") return;

  listening = true;
  document.addEventListener("visibilitychange", handleVisibilityChange);
  document.addEventListener("freeze", deactivatePage);
  document.addEventListener("resume", activateFromBrowserState);
  window.addEventListener(
    "wheel",
    activateFromBackgroundInteraction,
    passiveCaptureOptions,
  );
  window.addEventListener(
    "touchmove",
    activateFromBackgroundInteraction,
    passiveCaptureOptions,
  );
  window.addEventListener("focus", activateFromBrowserState);
  window.addEventListener("blur", deactivatePage);
  window.addEventListener("pagehide", deactivatePage);
  window.addEventListener("pageshow", activateFromBrowserState);
  publishPageActivity(readPageActivity());
}

function stopListening() {
  if (!listening || typeof window === "undefined") return;

  listening = false;
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  document.removeEventListener("freeze", deactivatePage);
  document.removeEventListener("resume", activateFromBrowserState);
  window.removeEventListener(
    "wheel",
    activateFromBackgroundInteraction,
    passiveCaptureOptions,
  );
  window.removeEventListener(
    "touchmove",
    activateFromBackgroundInteraction,
    passiveCaptureOptions,
  );
  window.removeEventListener("focus", activateFromBrowserState);
  window.removeEventListener("blur", deactivatePage);
  window.removeEventListener("pagehide", deactivatePage);
  window.removeEventListener("pageshow", activateFromBrowserState);
  clearBackgroundInteractionLease();
}

function subscribeToPageActivity(listener: PageActivityListener) {
  listeners.add(listener);
  startListening();

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stopListening();
  };
}

function getPageActivitySnapshot() {
  return currentPageActivity;
}

function getServerPageActivitySnapshot() {
  return false;
}

export function usePageActivity() {
  return useSyncExternalStore(
    subscribeToPageActivity,
    getPageActivitySnapshot,
    getServerPageActivitySnapshot,
  );
}
